import React, { useEffect, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Printer, Check, X } from "lucide-react";
import { format } from "date-fns";

const COURSE_TYPES = [
  { value: "single", label: "Single (1-on-1)" },
  { value: "group", label: "Group" },
];
const CONFIRMATIONS = [
  { value: "pending", label: "Pending", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "confirmed", label: "Confirmed", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "declined", label: "Declined", tone: "bg-red-50 text-red-700 border-red-200" },
];

function buildNoticeText(n) {
  const lines = [
    "✦ ─────────────────── ✦",
    "  Dawnville English Studio",
    "  · Course Invitation ·",
    "✦ ─────────────────── ✦",
    "",
    "Dear Parent,",
    "We are delighted to invite your child to the upcoming class detailed below.",
    "",
    `Class      ·  ${n.class_name || "—"}`,
    `Type       ·  ${n.class_type === "single" ? "Single (1-on-1)" : n.class_type === "group" ? "Group" : "—"}`,
    n.start_date ? `Start date ·  ${n.start_date}` : null,
    n.scheduled_time ? `Schedule   ·  ${n.scheduled_time}` : null,
    "",
    n.materials ? `Materials\n${n.materials}` : null,
    "",
    n.students && n.students.length ? "Students" : null,
    ...(n.students || []).map((s) => `  · ${s.name}${s.contact ? ` (${s.contact})` : ""} — ${s.confirmation}`),
    "",
    n.notes ? `A note from us\n${n.notes}` : null,
    "",
    "Kindly RSVP to confirm your child's attendance.",
    "With warm regards,",
    "— Dawnville English Studio",
    "✦ ─────────────────── ✦",
  ];
  return lines.filter(l => l !== null).join("\n");
}

// Subtle SVG flourish used as a decorative divider on the invitation card
function Flourish({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 220 16" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
        <path d="M2 8 H86" />
        <path d="M134 8 H218" />
        <circle cx="110" cy="8" r="3.2" />
        <path d="M96 8 q6 -6 14 0 q6 6 14 0" />
      </g>
    </svg>
  );
}

function CornerOrnament({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
        <path d="M4 24 Q4 4 24 4" />
        <path d="M10 24 Q10 10 24 10" />
        <circle cx="24" cy="4" r="1.4" fill="currentColor" />
        <circle cx="4" cy="24" r="1.4" fill="currentColor" />
      </g>
    </svg>
  );
}

function NoticeCard({ notice, onPatch, onDelete }) {
  const [draft, setDraft] = useState(notice);
  const [studentDraft, setStudentDraft] = useState({ name: "", contact: "" });

  function commit(patch) {
    const next = { ...draft, ...patch };
    setDraft(next);
    onPatch(notice.id, patch);
  }

  function addStudent() {
    if (!studentDraft.name.trim()) { toast.error("Student name is required"); return; }
    const next = [...(draft.students || []), { ...studentDraft, confirmation: "pending" }];
    commit({ students: next });
    setStudentDraft({ name: "", contact: "" });
  }
  function removeStudent(idx) {
    const next = (draft.students || []).filter((_, i) => i !== idx);
    commit({ students: next });
  }
  function patchStudent(idx, patch) {
    const next = (draft.students || []).map((s, i) => i === idx ? { ...s, ...patch } : s);
    commit({ students: next });
  }

  function copyText() {
    const text = buildNoticeText(draft);
    navigator.clipboard?.writeText(text);
    toast.success("Notice copied — paste into WeChat / Line / email");
  }
  function printNotice() {
    const studentRows = (draft.students || []).map(s => `
      <tr>
        <td>${(s.name || "").replace(/[<>]/g, '')}</td>
        <td>${(s.contact || "—").replace(/[<>]/g, '')}</td>
        <td><span class="pill pill-${s.confirmation || "pending"}">${(s.confirmation || "pending").toUpperCase()}</span></td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${(draft.class_name || "Course Invitation").replace(/[<>]/g, '')}</title>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet">
      <style>
        :root{--terra:#C8553D;--forest:#2C5545;--cream:#FDFBF7;--paper:#F8F2E6;--muted:#7a7268;--ink:#1f1b16;--rule:#E5D9C2}
        *{box-sizing:border-box}
        html,body{margin:0;padding:0;background:#efe6d4;font-family:'Manrope',system-ui,sans-serif;color:var(--ink);-webkit-print-color-adjust:exact;print-color-adjust:exact}
        .page{max-width:720px;margin:36px auto;padding:0 20px}
        .card{position:relative;background:var(--cream);border:1px solid var(--rule);box-shadow:0 30px 60px -30px rgba(60,40,20,0.25);padding:56px 56px 44px;border-radius:6px;overflow:hidden}
        .card::before,.card::after{content:"";position:absolute;width:120px;height:120px;border:1px solid var(--terra);opacity:0.45;border-radius:2px}
        .card::before{top:14px;left:14px;border-right:none;border-bottom:none}
        .card::after{bottom:14px;right:14px;border-left:none;border-top:none}
        .corner{position:absolute;width:46px;height:46px;color:var(--terra);opacity:0.7}
        .corner.tr{top:18px;right:18px;transform:rotate(90deg)}
        .corner.bl{bottom:18px;left:18px;transform:rotate(-90deg)}
        .monogram{display:flex;align-items:center;justify-content:center;flex-direction:column;text-align:center;color:var(--terra)}
        .monogram .seal{width:54px;height:54px;border:1.5px solid var(--terra);border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:'Outfit',serif;font-weight:600;font-size:22px;letter-spacing:0;color:var(--terra);background:var(--paper)}
        .monogram .name{font-family:'Outfit',serif;font-weight:500;letter-spacing:0.32em;font-size:11px;text-transform:uppercase;margin-top:14px;color:var(--forest)}
        .monogram .sub{font-family:'Outfit',serif;font-style:italic;color:var(--muted);font-size:13px;margin-top:4px}
        .flourish{display:block;margin:18px auto 22px;width:220px;height:16px;color:var(--terra);opacity:0.7}
        h1.title{font-family:'Outfit',serif;font-weight:400;font-size:38px;line-height:1.15;text-align:center;margin:8px 0 6px;color:var(--ink);letter-spacing:-0.01em}
        h1.title em{font-style:italic;color:var(--terra);font-weight:500}
        .greeting{text-align:center;font-style:italic;color:var(--muted);max-width:520px;margin:6px auto 28px;line-height:1.7;font-size:14.5px}
        .meta{display:grid;grid-template-columns:1fr 1fr;gap:14px 36px;margin:8px 0 6px;padding:22px 28px;background:var(--paper);border:1px solid var(--rule);border-radius:10px}
        .meta .row{display:flex;flex-direction:column;gap:4px}
        .meta .label{font-family:'Outfit',serif;font-size:10px;letter-spacing:0.28em;text-transform:uppercase;color:var(--terra)}
        .meta .value{font-family:'Outfit',serif;font-size:17px;color:var(--ink)}
        .section-label{font-family:'Outfit',serif;font-size:10px;letter-spacing:0.28em;text-transform:uppercase;color:var(--terra);margin:26px 0 10px;text-align:center}
        .materials,.notes{background:var(--paper);border:1px dashed var(--rule);border-radius:10px;padding:18px 22px;white-space:pre-wrap;font-size:14px;line-height:1.65;color:var(--ink)}
        table{width:100%;border-collapse:collapse;margin-top:6px;background:var(--paper);border:1px solid var(--rule);border-radius:10px;overflow:hidden}
        th,td{text-align:left;padding:12px 16px;border-bottom:1px solid var(--rule);font-size:13.5px}
        th{font-family:'Outfit',serif;color:var(--muted);text-transform:uppercase;letter-spacing:0.18em;font-size:10px;font-weight:500;background:rgba(200,85,61,0.05)}
        tr:last-child td{border-bottom:none}
        .pill{display:inline-block;padding:3px 10px;border-radius:999px;font-size:10px;letter-spacing:0.14em;font-weight:600;border:1px solid}
        .pill-confirmed{background:#ecf6ef;color:#1f6a3a;border-color:#cde6d5}
        .pill-pending{background:#fdf6e7;color:#8a6520;border-color:#f0e1b5}
        .pill-declined{background:#fdecec;color:#8a2a2a;border-color:#f0c8c8}
        .rsvp{margin-top:34px;padding:24px;border:1px solid var(--rule);border-radius:10px;background:linear-gradient(180deg,rgba(200,85,61,0.05),rgba(200,85,61,0));text-align:center}
        .rsvp .ask{font-family:'Outfit',serif;font-size:16px;color:var(--ink);margin:0}
        .rsvp .sign{font-family:'Outfit',serif;font-style:italic;color:var(--terra);font-size:18px;margin-top:8px}
        .footer{margin-top:24px;text-align:center;font-size:11px;letter-spacing:0.18em;color:var(--muted);text-transform:uppercase}
        @media print{html,body{background:white}.page{margin:0;padding:0}.card{box-shadow:none;border:none}}
      </style></head><body>
      <div class="page"><div class="card">
        <svg class="corner tr" viewBox="0 0 48 48"><g fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"><path d="M4 24 Q4 4 24 4"/><path d="M10 24 Q10 10 24 10"/><circle cx="24" cy="4" r="1.4" fill="currentColor"/><circle cx="4" cy="24" r="1.4" fill="currentColor"/></g></svg>
        <svg class="corner bl" viewBox="0 0 48 48"><g fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"><path d="M4 24 Q4 4 24 4"/><path d="M10 24 Q10 10 24 10"/><circle cx="24" cy="4" r="1.4" fill="currentColor"/><circle cx="4" cy="24" r="1.4" fill="currentColor"/></g></svg>
        <div class="monogram">
          <div class="seal">D</div>
          <div class="name">Dawnville · English · Studio</div>
          <div class="sub">— A Course Invitation —</div>
        </div>
        <svg class="flourish" viewBox="0 0 220 16"><g fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round"><path d="M2 8 H86"/><path d="M134 8 H218"/><circle cx="110" cy="8" r="3.2"/><path d="M96 8 q6 -6 14 0 q6 6 14 0"/></g></svg>
        <h1 class="title">You are warmly invited to <em>${(draft.class_name || "our upcoming class").replace(/[<>]/g, '')}</em></h1>
        <p class="greeting">Dear Parent, we are delighted to share the details of your child's next class with you. Please review and kindly confirm attendance at your earliest convenience.</p>
        <div class="meta">
          <div class="row"><span class="label">Class type</span><span class="value">${draft.class_type === "single" ? "Single · 1-on-1" : "Group"}</span></div>
          <div class="row"><span class="label">Start date</span><span class="value">${(draft.start_date || "—").replace(/[<>]/g, '')}</span></div>
          <div class="row"><span class="label">Schedule</span><span class="value">${(draft.scheduled_time || "—").replace(/[<>]/g, '')}</span></div>
          <div class="row"><span class="label">Students</span><span class="value">${(draft.students || []).length} attending</span></div>
        </div>
        ${draft.materials ? `<div class="section-label">— Materials —</div><div class="materials">${draft.materials.replace(/[<>]/g, '')}</div>` : ""}
        ${(draft.students || []).length ? `<div class="section-label">— Students &amp; RSVP —</div>
          <table><thead><tr><th>Name</th><th>Contact</th><th>Confirmation</th></tr></thead><tbody>${studentRows}</tbody></table>` : ""}
        ${draft.notes ? `<div class="section-label">— A note from us —</div><div class="notes">${draft.notes.replace(/[<>]/g, '')}</div>` : ""}
        <div class="rsvp">
          <p class="ask">Kindly RSVP to confirm your child's attendance.</p>
          <div class="sign">With warm regards,<br/>Dawnville English Studio</div>
        </div>
        <div class="footer">✦ &nbsp; Dawnville · English · Studio &nbsp; ✦</div>
      </div></div>
      <script>window.onload=()=>setTimeout(()=>window.print(),250)<\/script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  }

  return (
    <div data-testid={`notice-${notice.id}`} className="relative bg-[#FBF6EC] rounded-[8px] border border-[#E5D9C2] shadow-[0_24px_50px_-30px_rgba(60,40,20,0.25)] overflow-hidden">
      {/* Decorative inner double-rule frame */}
      <div className="pointer-events-none absolute inset-3 border border-[hsl(var(--primary))]/30 rounded-[4px]"></div>
      {/* Corner ornaments */}
      <CornerOrnament className="absolute top-3 left-3 w-9 h-9 text-[hsl(var(--primary))]/70" />
      <CornerOrnament className="absolute top-3 right-3 w-9 h-9 text-[hsl(var(--primary))]/70 rotate-90" />
      <CornerOrnament className="absolute bottom-3 left-3 w-9 h-9 text-[hsl(var(--primary))]/70 -rotate-90" />
      <CornerOrnament className="absolute bottom-3 right-3 w-9 h-9 text-[hsl(var(--primary))]/70 rotate-180" />

      <div className="relative px-6 md:px-10 pt-10 pb-8">
        {/* Invitation header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={copyText} data-testid={`notice-${notice.id}-copy`} className="rounded-full bg-white/60"><Copy className="w-3.5 h-3.5 mr-1.5" />Copy</Button>
            <Button size="sm" variant="outline" onClick={printNotice} data-testid={`notice-${notice.id}-print`} className="rounded-full bg-white/60"><Printer className="w-3.5 h-3.5 mr-1.5" />Print / PDF</Button>
          </div>
          <Button size="sm" variant="ghost" onClick={()=>onDelete(notice.id)} data-testid={`notice-${notice.id}-delete`} className="text-[hsl(var(--destructive))] hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
        </div>

        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-[hsl(var(--primary))] bg-[#F8F2E6] text-[hsl(var(--primary))] font-display text-2xl">D</div>
          <p className="font-display tracking-[0.32em] text-[11px] uppercase text-[hsl(var(--secondary))] mt-4">Dawnville · English · Studio</p>
          <p className="font-display italic text-[hsl(var(--muted-foreground))] text-sm mt-1">— A Course Invitation —</p>
          <Flourish className="mx-auto mt-5 w-[220px] h-4 text-[hsl(var(--primary))]/70" />
          <h2 className="font-display text-2xl md:text-[28px] leading-tight mt-2 text-[hsl(var(--foreground))]">
            You are warmly invited to{" "}
            <em className="not-italic italic-quote text-[hsl(var(--primary))]" style={{ fontStyle: "italic" }}>
              {draft.class_name || "our upcoming class"}
            </em>
          </h2>
          <p className="italic text-[hsl(var(--muted-foreground))] text-sm max-w-lg mx-auto mt-3 leading-relaxed">
            Dear Parent, we are delighted to share the details of your child&apos;s next class with you. Please review and kindly confirm attendance.
          </p>
        </div>

        {/* Editable class name & meta */}
        <div className="mt-7 grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-12">
            <Label className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">Class name</Label>
            <Input value={draft.class_name || ""} onChange={(e)=>setDraft({...draft, class_name: e.target.value})} onBlur={(e)=>commit({ class_name: e.target.value })} data-testid={`notice-${notice.id}-name`} className="mt-2 bg-white/70 border-[#E5D9C2] font-display text-lg" />
          </div>
          <div className="md:col-span-4">
            <Label className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">Class type</Label>
            <Select value={draft.class_type || "group"} onValueChange={(v)=>commit({ class_type: v })}>
              <SelectTrigger data-testid={`notice-${notice.id}-type`} className="mt-2 bg-white/70 border-[#E5D9C2]"><SelectValue /></SelectTrigger>
              <SelectContent>{COURSE_TYPES.map(c=><SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4">
            <Label className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">Start date</Label>
            <Input type="date" value={draft.start_date || ""} onChange={(e)=>setDraft({...draft, start_date: e.target.value})} onBlur={(e)=>commit({ start_date: e.target.value || null })} data-testid={`notice-${notice.id}-date`} className="mt-2 bg-white/70 border-[#E5D9C2]" />
          </div>
          <div className="md:col-span-4">
            <Label className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">Schedule</Label>
            <Input value={draft.scheduled_time || ""} onChange={(e)=>setDraft({...draft, scheduled_time: e.target.value})} onBlur={(e)=>commit({ scheduled_time: e.target.value })} placeholder="e.g. Mon · Wed · Fri 16:00 - 17:30" data-testid={`notice-${notice.id}-time`} className="mt-2 bg-white/70 border-[#E5D9C2]" />
          </div>
          <div className="md:col-span-12">
            <Label className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">Materials</Label>
            <Textarea rows={2} value={draft.materials || ""} onChange={(e)=>setDraft({...draft, materials: e.target.value})} onBlur={(e)=>commit({ materials: e.target.value })} placeholder="e.g. Cambridge PET Workbook ch. 4–6, Oxford Reading Tree Level 8" data-testid={`notice-${notice.id}-materials`} className="mt-2 bg-white/70 border-[#E5D9C2]" />
          </div>
          <div className="md:col-span-12">
            <Label className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">A note from us (optional)</Label>
            <Textarea rows={2} value={draft.notes || ""} onChange={(e)=>setDraft({...draft, notes: e.target.value})} onBlur={(e)=>commit({ notes: e.target.value })} data-testid={`notice-${notice.id}-notes`} className="mt-2 bg-white/70 border-[#E5D9C2]" />
          </div>
        </div>

        {/* Students section with invitation styling */}
        <div className="mt-7">
          <div className="text-center">
            <p className="font-display text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">— Students &amp; RSVP —</p>
          </div>
          <ul className="mt-3 divide-y divide-[#E5D9C2] rounded-xl border border-[#E5D9C2] bg-white/60">
            {(draft.students || []).map((s, idx) => {
              const tone = CONFIRMATIONS.find(c => c.value === (s.confirmation || "pending"))?.tone;
              return (
                <li key={idx} data-testid={`notice-${notice.id}-student-${idx}`} className="p-2.5 grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                  <Input value={s.name} onChange={(e)=>patchStudent(idx, { name: e.target.value })} className="sm:col-span-4 h-8 text-sm bg-white" data-testid={`notice-${notice.id}-student-${idx}-name`} />
                  <Input value={s.contact || ""} onChange={(e)=>patchStudent(idx, { contact: e.target.value })} placeholder="WeChat / phone" className="sm:col-span-4 h-8 text-sm bg-white" data-testid={`notice-${notice.id}-student-${idx}-contact`} />
                  <Select value={s.confirmation || "pending"} onValueChange={(v)=>patchStudent(idx, { confirmation: v })}>
                    <SelectTrigger data-testid={`notice-${notice.id}-student-${idx}-confirm`} className={`sm:col-span-3 h-8 px-2 text-xs border ${tone}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{CONFIRMATIONS.map(c=><SelectItem key={c.value} value={c.value} data-testid={`notice-${notice.id}-student-${idx}-confirm-${c.value}`}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={()=>removeStudent(idx)} className="sm:col-span-1 text-[hsl(var(--destructive))]" data-testid={`notice-${notice.id}-student-${idx}-remove`}><X className="w-4 h-4" /></Button>
                </li>
              );
            })}
            {(draft.students || []).length === 0 && <li className="p-3 text-xs text-[hsl(var(--muted-foreground))] italic text-center">No students yet — add the first one below.</li>}
          </ul>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
            <Input placeholder="Student name" value={studentDraft.name} onChange={(e)=>setStudentDraft({...studentDraft, name: e.target.value})} className="sm:col-span-5 h-8 text-sm bg-white" data-testid={`notice-${notice.id}-new-student-name`} />
            <Input placeholder="Contact (optional)" value={studentDraft.contact} onChange={(e)=>setStudentDraft({...studentDraft, contact: e.target.value})} className="sm:col-span-5 h-8 text-sm bg-white" data-testid={`notice-${notice.id}-new-student-contact`} />
            <Button size="sm" onClick={addStudent} className="sm:col-span-2 rounded-full bg-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/90 text-[hsl(var(--background))]" data-testid={`notice-${notice.id}-add-student`}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
          </div>
        </div>

        {/* RSVP footer */}
        <div className="mt-7 rounded-xl border border-[#E5D9C2] bg-gradient-to-b from-[hsl(var(--primary))]/[0.06] to-transparent px-6 py-5 text-center">
          <p className="font-display text-[15px] text-[hsl(var(--foreground))]">Kindly RSVP to confirm your child&apos;s attendance.</p>
          <p className="font-display italic text-[hsl(var(--primary))] text-lg mt-1">With warm regards, Dawnville English Studio</p>
        </div>
        <div className="mt-4 text-center text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]">✦ &nbsp; Dawnville · English · Studio &nbsp; ✦</div>
      </div>
    </div>
  );
}

export default function NoticesPanel() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ class_name: "", class_type: "group", start_date: "", scheduled_time: "", materials: "" });

  async function load() {
    try { const { data } = await api.get("/admin/notices"); setItems(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function create(e) {
    e.preventDefault();
    if (!draft.class_name.trim()) { toast.error("Class name is required"); return; }
    try {
      const payload = { ...draft };
      if (!payload.start_date) delete payload.start_date;
      await api.post("/admin/notices", payload);
      toast.success("Notice created");
      setDraft({ class_name: "", class_type: "group", start_date: "", scheduled_time: "", materials: "" });
      setCreating(false); load();
    } catch (e) { toast.error(getErrorDetail(e)); }
  }
  async function patchNotice(id, patch) {
    try {
      const { data } = await api.patch(`/admin/notices/${id}`, patch);
      setItems(prev => prev.map(n => n.id === id ? data : n));
    } catch (e) { toast.error(getErrorDetail(e)); load(); }
  }
  async function delNotice(id) {
    if (!window.confirm("Delete this notice?")) return;
    try { await api.delete(`/admin/notices/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  return (
    <div data-testid="admin-notices-panel" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Course notices · {items.length}</div>
        <Button size="sm" onClick={()=>setCreating(v=>!v)} data-testid="notice-new-toggle" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
          {creating ? <><X className="w-3.5 h-3.5 mr-1.5" />Cancel</> : <><Plus className="w-3.5 h-3.5 mr-1.5" />New notice</>}
        </Button>
      </div>
      {creating && (
        <form onSubmit={create} className="bg-white border border-[hsl(var(--border))] rounded-2xl p-5 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="notice-create-form">
          <div className="md:col-span-4"><Label className="text-xs">Class name</Label><Input required value={draft.class_name} onChange={(e)=>setDraft({...draft, class_name: e.target.value})} data-testid="notice-new-name" className="mt-1" /></div>
          <div className="md:col-span-3"><Label className="text-xs">Class type</Label>
            <Select value={draft.class_type} onValueChange={(v)=>setDraft({...draft, class_type: v})}>
              <SelectTrigger data-testid="notice-new-type" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{COURSE_TYPES.map(c=><SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label className="text-xs">Start date</Label><Input type="date" value={draft.start_date} onChange={(e)=>setDraft({...draft, start_date: e.target.value})} data-testid="notice-new-date" className="mt-1" /></div>
          <div className="md:col-span-3"><Label className="text-xs">Scheduled time</Label><Input value={draft.scheduled_time} onChange={(e)=>setDraft({...draft, scheduled_time: e.target.value})} placeholder="e.g. Mon · Wed 16:00 - 17:30" data-testid="notice-new-time" className="mt-1" /></div>
          <div className="md:col-span-12"><Label className="text-xs">Materials</Label><Textarea rows={2} value={draft.materials} onChange={(e)=>setDraft({...draft, materials: e.target.value})} data-testid="notice-new-materials" className="mt-1" /></div>
          <div className="md:col-span-12"><Button type="submit" data-testid="notice-new-submit" className="rounded-full bg-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/90 text-[hsl(var(--background))]"><Check className="w-4 h-4 mr-1.5" />Create notice</Button></div>
        </form>
      )}
      <p className="text-xs text-[hsl(var(--muted-foreground))] italic">Notices are auto-created whenever a Planned or Active class is added in the Schedules tab. You can also create one manually above. Edits autosave on blur.</p>
      {items.map((n) => <NoticeCard key={n.id} notice={n} onPatch={patchNotice} onDelete={delNotice} />)}
      {items.length === 0 && <div className="p-10 text-center text-[hsl(var(--muted-foreground))] bg-white rounded-2xl border border-[hsl(var(--border))]">No notices yet.</div>}
    </div>
  );
}
